import { useState, useEffect, useCallback, useMemo, useRef, type KeyboardEvent } from 'react'
import './Game.css'
import { getGameData, getClubsData, getPlayerName } from '../services/gameDataService'
import { gameConfig } from '../config/gameConfig'
import { trackGameStart, trackGameComplete } from '../utils/analytics'
import { getTodayGameResult, getGameNumberForDate, getGameKeyForDate, formatDateForStorage, getGameResultForDate, type GameResult } from '../utils/dateUtils'

interface GameProps {
  onGameNumberChange?: (gameNumber: number) => void
  onGameStatusChange?: (isCompleted: boolean, gameResult?: { isWin: boolean, playersRevealed: number, totalPlayers: number, theme: string, guessedCorrectly?: string, currentStreak: number }) => void
  gameDate?: Date // Optional specific date to play
  isReview?: boolean // Whether this is a review of a completed game
}

interface GameState {
  guessedLetters: string[]
  gameStatus: 'playing' | 'won' | 'lost' | 'theme-guess'
  livesRemaining: number
  incorrectThemeGuesses: string[]
  correctGuess: string | null
  currentGameIndex: number
  originallyGuessedLetters?: string[]
  isDaily?: boolean
  gameDate?: string
}

interface GameStats {
  totalGames: number
  wins: number
  losses: number
  currentWinStreak: number
  bestWinStreak: number
  winsByPlayersRevealed: {
    1: number
    2: number
    3: number
    4: number
    5: number
  }
}

function Game({ onGameNumberChange, onGameStatusChange, gameDate, isReview }: GameProps) {
  // Daily game logic - use current game number for today or specified date
  const targetDate = useMemo(() => gameDate || new Date(), [gameDate])
  const currentGameNumber = useMemo(() => getGameNumberForDate(targetDate), [targetDate])
  const currentGameIndex = useMemo(() => currentGameNumber - 1, [currentGameNumber]) // Convert to 0-based index

  // Use actual date for storage key, not literal "today"
  const STORAGE_KEY = useMemo(() => 
    `${gameConfig.storagePrefix}-game-state-${formatDateForStorage(targetDate)}`, 
    [targetDate]
  )
  const STATS_KEY = `${gameConfig.storagePrefix}-game-stats`
  
  // Debug logging
  console.log('Game component initializing with:', {
    gameDate,
    isReview,
    targetDate: targetDate.toDateString(),
    STORAGE_KEY,
    currentGameIndex,
    currentGameNumber
  })
  
  // Log review mode for debugging
  useEffect(() => {
    if (isReview) {
      console.log('Game component loaded in review mode for date:', gameDate)
    }
  }, [isReview, gameDate])
  
  // Load game state from localStorage first to get the current game index
  const loadGameState = useCallback((): GameState | null => {
    try {
      console.log('Loading game state from:', STORAGE_KEY)
      const saved = localStorage.getItem(STORAGE_KEY)
      console.log('Raw saved data:', saved)
      if (saved) {
        const parsed = JSON.parse(saved)
        console.log('Parsed saved state:', parsed)
        return parsed
      }
    } catch (error) {
      console.error('Error loading game state:', error)
    }
    console.log('No saved state found')
    return null
  }, [STORAGE_KEY])
  
  // Get game data using the service
  const gamesData = useMemo(() => getGameData(), [])
  
  // Daily game logic - use current game number for today or specified date
  const currentGame = useMemo(() => {
    return gamesData[currentGameIndex % gamesData.length]
  }, [currentGameIndex, gamesData])
  
  const targetWords = useMemo(() => 
    currentGame.players.map(player => getPlayerName(player).toUpperCase()), 
    [currentGame]
  )
  const correctTheme = currentGame.club || currentGame.clubName!
  
  // Function to identify non-letter characters that should be pre-revealed
  const getNonLetterCharacters = useCallback((words: string[]) => {
    const nonLetters = new Set<string>()
    words.forEach(word => {
      word.split('').forEach(char => {
        if (!char.match(/[A-Z]/)) {
          nonLetters.add(char)
        }
      })
    })
    return nonLetters
  }, [])
  
  // Get all non-letter characters from target words that should be pre-revealed
  const preRevealedChars = useMemo(() => getNonLetterCharacters(targetWords), [targetWords, getNonLetterCharacters])
  
  // Monitor for date changes to refresh the game
  useEffect(() => {
    // Don't monitor date changes when viewing historical games or in review mode
    if (gameDate || isReview) {
      return
    }
    
    // Use a ref to track the current date across interval calls
    const currentDateRef = { current: new Date().toDateString() }
    
    const checkForNewDay = () => {
      const newDate = new Date().toDateString()
      if (newDate !== currentDateRef.current) {
        // Date has changed - completely reset the game
        console.log('Date change detected:', currentDateRef.current, '->', newDate)
        
        console.log('Game localStorage cleared, forcing page reload')
        
        // Force a page reload to completely reset all state
        window.location.reload()
      }
    }
    
    // Check every 2 seconds for date changes (more frequent)
    const interval = setInterval(checkForNewDay, 2000)
    
    return () => clearInterval(interval)
  }, [STORAGE_KEY, gameDate, isReview])
  
  // Notify parent when game number changes
  useEffect(() => {
    onGameNumberChange?.(currentGameNumber)
  }, [currentGameNumber, onGameNumberChange])

  // Track game start for new games (only when no saved state exists)
  useEffect(() => {
    const savedState = loadGameState()
    if (!savedState || savedState.gameStatus === 'playing') {
      // Only track if this is a fresh game start
      const gameDate = formatDateForStorage(targetDate)
      trackGameStart(gameDate)
      // Reset the game start time for analytics
      gameStartTime.current = Date.now()
    }
  }, [currentGameNumber, loadGameState, targetDate])
  
  // Load game stats from localStorage
  const loadGameStats = useCallback((): GameStats => {
    try {
      const saved = localStorage.getItem(STATS_KEY)
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (error) {
      console.error('Error loading game stats:', error)
    }
    // Return default stats if none exist
    return {
      totalGames: 0,
      wins: 0,
      losses: 0,
      currentWinStreak: 0,
      bestWinStreak: 0,
      winsByPlayersRevealed: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    }
  }, [STATS_KEY])
  
  // Save game stats to localStorage
  const saveGameStats = useCallback((stats: GameStats) => {
    try {
      localStorage.setItem(STATS_KEY, JSON.stringify(stats))
    } catch (error) {
      console.error('Error saving game stats:', error)
    }
  }, [STATS_KEY])
  
  // Record a completed game in stats
  const recordGameCompletion = useCallback((isWin: boolean, playersRevealedByUser: number) => {
    const currentStats = loadGameStats()
    
    // Handle win streak logic
    let newCurrentWinStreak = 0
    let newBestWinStreak = currentStats.bestWinStreak
    
    if (isWin) {
      // Increment current win streak
      newCurrentWinStreak = currentStats.currentWinStreak + 1
      // Update best win streak if current streak is better
      newBestWinStreak = Math.max(newBestWinStreak, newCurrentWinStreak)
    } else {
      // Reset current win streak on loss
      newCurrentWinStreak = 0
    }
    
    const newStats = {
      ...currentStats,
      totalGames: currentStats.totalGames + 1,
      wins: isWin ? currentStats.wins + 1 : currentStats.wins,
      losses: isWin ? currentStats.losses : currentStats.losses + 1,
      currentWinStreak: newCurrentWinStreak,
      bestWinStreak: newBestWinStreak
    }
    
    // Update the distribution by players revealed - only for wins and only for user-revealed players
    if (isWin && playersRevealedByUser > 0) {
      newStats.winsByPlayersRevealed = {
        ...currentStats.winsByPlayersRevealed,
        [playersRevealedByUser as keyof typeof currentStats.winsByPlayersRevealed]: 
          currentStats.winsByPlayersRevealed[playersRevealedByUser as keyof typeof currentStats.winsByPlayersRevealed] + 1
      }
    }
    
    saveGameStats(newStats)
    setCurrentStats(newStats)
  }, [loadGameStats, saveGameStats])
  
  // Save game state to localStorage
  const saveGameState = useCallback((state: Partial<GameState>) => {
    try {
      const currentState = loadGameState()
      const targetDateString = formatDateForStorage(targetDate) // Use target date, not today
      const newState = { 
        ...currentState, 
        ...state, 
        currentGameIndex,
        gameDate: targetDateString, // Use target date for consistency
        isDaily: true // Mark as daily game
      }
      console.log("Saving game state to:", STORAGE_KEY)
      console.log("State being saved:", newState)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState))
      console.log("State saved successfully")
    } catch (error) {
      console.error('Error saving game state:', error)
    }
  }, [STORAGE_KEY, loadGameState, currentGameIndex, targetDate])
  
  // Initialize state from localStorage or defaults
  const initialSavedState = useMemo(() => {
    console.log('Loading game state for date:', gameDate ? formatDateForStorage(gameDate) : 'today')
    
    // When viewing a historical game, first try to load completed result, then in-progress state
    if (gameDate) {
      const result = getGameResultForDate(gameDate)
      console.log('Loading historical game for date:', gameDate, result)
      if (result && result.completed && result.gameNumber === currentGameNumber) {
        console.log('Loading completed historical game result', result.livesRemaining)
        return {
          guessedLetters: result.guessedLetters,
          gameStatus: result.gameStatus,
          livesRemaining: result.livesRemaining ?? (result.gameStatus === 'lost' ? 0 : 1), // Default to 1 life remaining for won games if not saved
          incorrectThemeGuesses: result.incorrectThemeGuesses,
          correctGuess: result.won ? (result.clubName || correctTheme) : null,
          currentGameIndex: currentGameIndex,
          originallyGuessedLetters: result.originallyGuessedLetters || [],
          isDaily: true,
          gameDate: formatDateForStorage(gameDate)
        }
      } else {
        console.log('No completed historical game found, checking for in-progress state')
        // For historical dates, also check for in-progress state
        const savedState = loadGameState()
        if (savedState && savedState.currentGameIndex === currentGameIndex) {
          console.log('Loading historical in-progress game state')
          return {
            ...savedState,
            gameDate: formatDateForStorage(gameDate) // Ensure correct date
          }
        }
        console.log('No historical game state found, starting fresh')
        return null
      }
    }
    
    // For current day games, try multiple sources in priority order:
    // 1. In-progress game state (highest priority for active games)
    // 2. Completed game result (if game was finished)
    
    const targetDateString = formatDateForStorage(targetDate)
    
    // First, try to load from in-progress game state
    const savedState = loadGameState()
    if (savedState && savedState.currentGameIndex === currentGameIndex) {
      console.log('Found saved in-progress game state')
      // Validate the date to ensure it's from the target date
      const savedDate = savedState.gameDate
      
      // Accept if dates match, or if no saved date (backward compatibility), or if we're on today and saved date is today's string format
      const isValidDate = savedDate === targetDateString || 
                         savedDate === targetDate.toDateString() || 
                         !savedDate
      
      if (isValidDate) {
        console.log('Loading saved in-progress game state for target date')
        return {
          ...savedState,
          gameDate: targetDateString // Ensure consistent date format
        }
      } else {
        console.log('Saved state is from different day, ignoring:', savedDate, 'vs', targetDateString)
      }
    }
    
    // Second, check if today's game has been completed
    const completedResult = getTodayGameResult()
    if (completedResult && completedResult.completed && completedResult.gameNumber === currentGameNumber) {
      console.log('Loading completed game result for today')
      return {
        guessedLetters: completedResult.guessedLetters,
        gameStatus: completedResult.gameStatus,
        livesRemaining: completedResult.livesRemaining ?? (completedResult.gameStatus === 'lost' ? 0 : 1), // Default to 1 life remaining for won games if not saved
        incorrectThemeGuesses: completedResult.incorrectThemeGuesses,
        correctGuess: completedResult.won ? (completedResult.clubName || correctTheme) : null,
        currentGameIndex: currentGameIndex,
        originallyGuessedLetters: completedResult.originallyGuessedLetters || [],
        isDaily: true,
        gameDate: targetDateString
      }
    }
    
    // If no valid saved state found, start fresh
    console.log('Starting fresh game for target date')
    return null
  }, [correctTheme, currentGameIndex, currentGameNumber, loadGameState, gameDate, targetDate])
  const [guessedLetters, setGuessedLetters] = useState<Set<string>>(() => {
    const initialLetters = initialSavedState ? new Set(initialSavedState.guessedLetters) : new Set<string>()
    // Add pre-revealed characters (punctuation) to initial guessed letters
    preRevealedChars.forEach(char => initialLetters.add(char))
    return initialLetters
  })
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'lost' | 'theme-guess'>(
    initialSavedState?.gameStatus || 'playing'
  )
  const [livesRemaining, setLivesRemaining] = useState<number>(
    initialSavedState?.livesRemaining || 5
  )
  const [themeGuess, setThemeGuess] = useState<string>('')
  const [showThemeOptions, setShowThemeOptions] = useState<boolean>(false)
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(-1)
  const [incorrectThemeGuesses, setIncorrectThemeGuesses] = useState<string[]>(
    initialSavedState?.incorrectThemeGuesses || []
  )
  const [correctGuess, setCorrectGuess] = useState<string | null>(
    initialSavedState?.correctGuess || null
  )
  const [originallyGuessedLetters, setOriginallyGuessedLetters] = useState<Set<string>>(() => {
    const savedOriginallyGuessed = initialSavedState?.originallyGuessedLetters
    let initialLetters: Set<string>
    
    if (savedOriginallyGuessed && savedOriginallyGuessed.length > 0) {
      // We have saved originally guessed letters data
      initialLetters = new Set(savedOriginallyGuessed)
    } else if (initialSavedState && initialSavedState.gameStatus === 'playing') {
      // For games in progress without original tracking, use current guessed letters
      initialLetters = new Set(initialSavedState.guessedLetters)
    } else if (initialSavedState && (initialSavedState.gameStatus === 'won' || initialSavedState.gameStatus === 'lost' || initialSavedState.gameStatus === 'theme-guess')) {
      // For completed games without original tracking, be conservative
      // Better to show letters as orange (auto-revealed) than incorrectly show them as green (user-guessed)
      initialLetters = new Set<string>()
    } else {
      // New game
      initialLetters = new Set<string>()
    }
    
    // Always add pre-revealed characters (punctuation) to initial guessed letters
    preRevealedChars.forEach(char => initialLetters.add(char))
    return initialLetters
  })
  
  const [currentStats, setCurrentStats] = useState<GameStats>(() => loadGameStats())
  
  // Track game start time for analytics
  const gameStartTime = useRef<number>(Date.now())
  
  // Create a ref for the keyboard to manage focus
  const keyboardRef = useRef<HTMLDivElement>(null)
  
  // Calculate how many players were fully revealed by user's own guesses
  const calculatePlayersRevealedByUser = useCallback(() => {
    let playersRevealedByUser = 0
    
    targetWords.forEach(word => {
      // Check if all letters in this player's name were originally guessed by the user
      const allLettersGuessedByUser = word.split('').every(letter => {
        // Pre-revealed characters (punctuation) don't count against the user
        if (preRevealedChars.has(letter)) {
          return true
        }
        // Check if this letter was originally guessed by the user
        return originallyGuessedLetters.has(letter)
      })
      
      if (allLettersGuessedByUser) {
        playersRevealedByUser++
      }
    })
    
    return playersRevealedByUser
  }, [targetWords, originallyGuessedLetters, preRevealedChars])
  
  // iOS Safari scroll fix - ensure proper scroll position when game state changes
  useEffect(() => {
    // Only apply scroll fix on mobile Safari
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent)
    if (!isIOSSafari) return

    const contentArea = document.querySelector('.content-area') as HTMLElement
    if (!contentArea) return

    const handleScroll = () => {
      // Prevent scroll position from getting stuck
      if (contentArea.scrollTop < 0) {
        contentArea.scrollTop = 0
      }
    }

    contentArea.addEventListener('scroll', handleScroll, { passive: true })
    
    // Ensure content is visible when status changes to won/lost
    if (gameStatus === 'won' || gameStatus === 'lost') {
      setTimeout(() => {
        contentArea.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }, 150)
    }

    return () => {
      contentArea.removeEventListener('scroll', handleScroll)
    }
  }, [gameStatus])

  // Notify parent of game completion status when it changes
  useEffect(() => {
    if (gameStatus === 'won' || gameStatus === 'lost') {
      const playersRevealedByUser = calculatePlayersRevealedByUser()
      onGameStatusChange?.(true, {
        isWin: gameStatus === 'won',
        playersRevealed: playersRevealedByUser,
        totalPlayers: targetWords.length,
        theme: correctTheme,
        guessedCorrectly: correctGuess || undefined,
        currentStreak: currentStats.currentWinStreak
      })
    } else {
      // Game is not completed
      onGameStatusChange?.(false)
    }
  }, [gameStatus, calculatePlayersRevealedByUser, onGameStatusChange, targetWords.length, correctTheme, correctGuess, currentStats.currentWinStreak])
  
  // Auto-save game state when key properties change
  useEffect(() => {
    // Always save game state when it changes, even on initial load
    const currentState = {
      guessedLetters: Array.from(guessedLetters),
      gameStatus,
      livesRemaining,
      incorrectThemeGuesses,
      correctGuess,
      originallyGuessedLetters: Array.from(originallyGuessedLetters)
    }
    
    // Only save if we have meaningful state (not just the initial empty state)
    const hasProgress = guessedLetters.size > preRevealedChars.size || 
                       gameStatus !== 'playing' || 
                       livesRemaining < 5 || 
                       incorrectThemeGuesses.length > 0 ||
                       correctGuess !== null
    
    if (hasProgress) {
      console.log('Auto-saving game state:', currentState)
      saveGameState(currentState)
    }
  }, [guessedLetters, gameStatus, livesRemaining, incorrectThemeGuesses, correctGuess, originallyGuessedLetters, saveGameState, preRevealedChars.size])
  
  const themeOptions = useMemo(() => {
    return getClubsData().map(club => club.name).sort()
  }, [])

  const topRow = ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P']
  const middleRow = ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L']
  const bottomRow = ['Z', 'X', 'C', 'V', 'B', 'N', 'M']

  const checkWinCondition = useCallback((letters: Set<string>) => {
    // Don't override the game status if the game is already completed
    if (gameStatus === 'won' || gameStatus === 'lost') {
      return
    }
    
    // Get all unique letters from all target words (since all are visible now)
    const allRequiredLetters = new Set<string>()
    targetWords.forEach(word => {
      word.split('').forEach(letter => allRequiredLetters.add(letter))
    })
    
    // Check if all required letters have been guessed
    const hasAllLetters = Array.from(allRequiredLetters).every(letter => letters.has(letter))
    
    if (hasAllLetters) {
      // All words are complete - go to theme guessing
      setGameStatus('theme-guess')
      
      // Save state immediately when transitioning to theme-guess
      const stateToSave = {
        gameStatus: 'theme-guess' as GameState['gameStatus'],
        guessedLetters: Array.from(letters),
        originallyGuessedLetters: Array.from(originallyGuessedLetters)
      }
      saveGameState(stateToSave)
      
      // iOS Safari scroll fix - ensure content is visible when transitioning to theme guess
      setTimeout(() => {
        const contentArea = document.querySelector('.content-area')
        if (contentArea) {
          contentArea.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
        }
        // Clear theme guess with a slight delay to prevent keyboard interference
        setThemeGuess('')
        setShowThemeOptions(false)
        setSelectedOptionIndex(-1)
      }, 50)
    }
  }, [targetWords, saveGameState, gameStatus, originallyGuessedLetters])

  const handleLetterClick = useCallback((letter: string) => {
    if (gameStatus !== 'playing' || guessedLetters.has(letter) || preRevealedChars.has(letter)) return
    
    // Add letter to guessed letters
    const newGuessedLetters = new Set(guessedLetters)
    newGuessedLetters.add(letter)
    setGuessedLetters(newGuessedLetters)
    
    // Also track this as an originally guessed letter
    const newOriginallyGuessed = new Set(originallyGuessedLetters)
    newOriginallyGuessed.add(letter)
    setOriginallyGuessedLetters(newOriginallyGuessed)
    
    // Check if the letter exists in any of the target words
    const letterFoundInWords = targetWords.some(word => word.includes(letter))
    
    if (letterFoundInWords) {
      // Correct guess - immediately save state and check win condition
      
      // Save state immediately after correct guess
      const stateToSave = {
        guessedLetters: Array.from(newGuessedLetters),
        originallyGuessedLetters: Array.from(newOriginallyGuessed),
        gameStatus: gameStatus as GameState['gameStatus'],
        livesRemaining
      }
      saveGameState(stateToSave)
      
      checkWinCondition(newGuessedLetters)
    } else {
      // Incorrect guess - lose a life
      const newLivesRemaining = livesRemaining - 1
      setLivesRemaining(newLivesRemaining)
      
      // Save state immediately after incorrect guess
      const stateToSave = {
        guessedLetters: Array.from(newGuessedLetters),
        originallyGuessedLetters: Array.from(newOriginallyGuessed),
        livesRemaining: newLivesRemaining,
        gameStatus: gameStatus as GameState['gameStatus']
      }
      saveGameState(stateToSave)
      
      if (newLivesRemaining === 0) {
        // No lives left - reveal all letters and go to final club guess
        const allRequiredLetters = new Set<string>()
        targetWords.forEach(word => {
          word.split('').forEach(letter => allRequiredLetters.add(letter))
        })
        
        // Add all missing letters to guessed letters so they show up as orange
        const allLettersRevealed = new Set(newGuessedLetters)
        allRequiredLetters.forEach(letter => allLettersRevealed.add(letter))
        
        setGuessedLetters(allLettersRevealed)
        setGameStatus('theme-guess')
        
        // Save the complete state when transitioning to theme-guess
        const finalStateToSave = {
          gameStatus: 'theme-guess' as GameState['gameStatus'],
          guessedLetters: Array.from(allLettersRevealed),
          originallyGuessedLetters: Array.from(newOriginallyGuessed),
          livesRemaining: newLivesRemaining
        }
        saveGameState(finalStateToSave)
        
        // iOS Safari scroll fix - ensure content is visible when running out of lives
        setTimeout(() => {
          const contentArea = document.querySelector('.content-area')
          if (contentArea) {
            contentArea.scrollTo({
              top: 0,
              behavior: 'smooth'
            })
          }
          // Clear theme guess with a slight delay
          setThemeGuess('')
          setShowThemeOptions(false)
          setSelectedOptionIndex(-1)
        }, 50)
      }
    }
  }, [gameStatus, guessedLetters, livesRemaining, targetWords, checkWinCondition, saveGameState, preRevealedChars, originallyGuessedLetters])

  const isLetterInWords = (letter: string) => {
    // Pre-revealed characters (punctuation) are always considered "in words"
    if (preRevealedChars.has(letter)) {
      return true
    }
    return targetWords.some(word => word.includes(letter))
  }

  const getFilteredThemeOptions = () => {
    const isRunningGame = gameConfig.dataSource === 'running'
    const minLength = isRunningGame ? 1 : 2
    
    if (!themeGuess.trim() || themeGuess.trim().length < minLength) return []
    return themeOptions.filter(option => 
      option.toLowerCase().includes(themeGuess.trim().toLowerCase())
    )
  }

  const handleThemeGuess = (selectedTheme: string) => {
    setThemeGuess(selectedTheme)
    setShowThemeOptions(false)
    
    if (selectedTheme.toLowerCase() === correctTheme.toLowerCase()) {
      // Set the correct guess and clear the input
      setCorrectGuess(selectedTheme)
      setGameStatus('won')
      
      // Save state immediately for winning
      const winStateToSave = {
        correctGuess: selectedTheme,
        gameStatus: 'won' as GameState['gameStatus'],
        guessedLetters: Array.from(guessedLetters),
        originallyGuessedLetters: Array.from(originallyGuessedLetters)
      }
      saveGameState(winStateToSave)
      
      // Track game completion
      trackGameComplete(true)
      
      // Calculate players revealed by user's own guesses
      const playersRevealedByUser = calculatePlayersRevealedByUser()
      
      // Record the win in stats
      recordGameCompletion(true, playersRevealedByUser)
      
        // Mark the game as completed (won) for the specific date
        const gameResult: GameResult = {
          gameNumber: currentGameNumber,
          completed: true,
          won: true,
          clubName: correctTheme,
          playersRevealed: playersRevealedByUser,
          livesRemaining: livesRemaining,
          completedAt: new Date().toISOString(),
          guessedLetters: Array.from(guessedLetters),
          originallyGuessedLetters: Array.from(originallyGuessedLetters),
          incorrectThemeGuesses: incorrectThemeGuesses,
          gameStatus: 'won'
        }
        
        // Use appropriate storage key for the game date
        const gameKey = getGameKeyForDate(targetDate)
        localStorage.setItem(gameKey, JSON.stringify(gameResult))
        
        // Notify parent component of game completion
        onGameStatusChange?.(true, {
          isWin: true,
          playersRevealed: playersRevealedByUser,
          totalPlayers: targetWords.length,
          theme: correctTheme,
          guessedCorrectly: selectedTheme,
          currentStreak: currentStats.currentWinStreak + 1 // Add 1 because stats haven't been updated in UI yet
        })
        
        // iOS Safari scroll fix - scroll to top to prevent getting stuck
        setTimeout(() => {
          const contentArea = document.querySelector('.content-area')
          if (contentArea) {
            contentArea.scrollTo({
              top: 0,
              behavior: 'smooth'
            })
          }
          setThemeGuess('')
          setShowThemeOptions(false)
          setSelectedOptionIndex(-1)
        }, 100) // Small delay to ensure DOM updates are complete
    } else {
      // Add to incorrect guesses list
      const newIncorrectGuesses = [...incorrectThemeGuesses, selectedTheme]
      setIncorrectThemeGuesses(newIncorrectGuesses)
      
      // Any wrong club guess ends the game immediately
      setGameStatus('lost')
      
      // Save state immediately for losing
      const loseStateToSave = {
        incorrectThemeGuesses: newIncorrectGuesses,
        gameStatus: 'lost' as GameState['gameStatus'],
        guessedLetters: Array.from(guessedLetters),
        originallyGuessedLetters: Array.from(originallyGuessedLetters)
      }
      saveGameState(loseStateToSave)
      
      // Track game completion
      trackGameComplete(false)
      
      // Calculate players revealed by user's own guesses (even though they lost)
      const playersRevealedByUser = calculatePlayersRevealedByUser()
      
      // Record the loss in stats
      recordGameCompletion(false, playersRevealedByUser)
      
      // Mark the game as completed (lost) for the specific date
      const gameResult: GameResult = {
        gameNumber: currentGameNumber,
        completed: true,
        won: false,
        clubName: correctTheme,
        playersRevealed: playersRevealedByUser,
        livesRemaining: livesRemaining,
        completedAt: new Date().toISOString(),
        guessedLetters: Array.from(guessedLetters),
        originallyGuessedLetters: Array.from(originallyGuessedLetters),
        incorrectThemeGuesses: newIncorrectGuesses,
        gameStatus: 'lost'
      }
      
      // Use appropriate storage key for the game date
      const gameKey = getGameKeyForDate(targetDate)
      localStorage.setItem(gameKey, JSON.stringify(gameResult))
      
      // Notify parent component of game completion
      onGameStatusChange?.(true, {
        isWin: false,
        playersRevealed: playersRevealedByUser,
        totalPlayers: targetWords.length,
        theme: correctTheme,
        currentStreak: 0 // Loss breaks the streak
      })
    }
  }

  const handleThemeInputChange = (value: string) => {
    setThemeGuess(value)
    setShowThemeOptions(true)
    setSelectedOptionIndex(-1) // Reset selection when typing
  }

  const handleThemeKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const filteredOptions = getFilteredThemeOptions()
    
    if (filteredOptions.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setSelectedOptionIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        event.preventDefault()
        setSelectedOptionIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        )
        break
      case 'Enter':
        event.preventDefault()
        if (selectedOptionIndex >= 0 && selectedOptionIndex < filteredOptions.length) {
          // User has selected an option with arrow keys
          handleThemeGuess(filteredOptions[selectedOptionIndex])
        } else if (filteredOptions.length === 1) {
          // Only one option available, select it automatically
          handleThemeGuess(filteredOptions[0])
        }
        break
      case 'Escape':
        setShowThemeOptions(false)
        setSelectedOptionIndex(-1)
        break
    }
  }

  // Handle physical keyboard input
  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (gameStatus !== 'playing') return
      
      const key = event.key.toUpperCase()
      
      // Handle letter keys
      if (key.match(/^[A-Z]$/)) {
        handleLetterClick(key)
      }
    }

    // Add event listener when component mounts
    window.addEventListener('keydown', handleKeyDown)

    // Clean up event listener when component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [gameStatus, handleLetterClick]) // Include handleLetterClick dependency

  const getLetterStatus = (letter: string) => {
    if (!guessedLetters.has(letter)) return ''
    return isLetterInWords(letter) ? 'correct' : 'absent'
  }

  const renderWordGrid = () => {
    // All words are always visible now
    const isWon = !!correctGuess
    const isLost = gameStatus === 'lost'
    const wordsToShow = targetWords // Always show all words
    const isThemeGuessing = gameStatus === 'theme-guess'
    const isOutOfLives = livesRemaining === 0
    const showFullPlayerNames = isWon || isLost // Show full player names when the game is completed (won or lost)
    const isRunningGame = gameConfig.dataSource === 'running'
    
    // Check if all words are complete (natural progression to theme guess)
    const allWordsComplete = targetWords.every(word => {
      return word.split('').every(letter => guessedLetters.has(letter))
    })
    
    // Helper function to get full player name
    const getFullPlayerName = (player: { name?: string; surname: string }) => {
      return player.name || player.surname
    }
    
    // Helper function to check if word should be split
    const shouldSplitWord = (word: string) => {
      return isRunningGame && word.length > 13 && word.includes(' ')
    }
    
    // Helper function to split word at first space
    const splitWord = (word: string) => {
      const spaceIndex = word.indexOf(' ')
      return [word.slice(0, spaceIndex), word.slice(spaceIndex + 1)]
    }
    
    return wordsToShow.map((word: string, wordIndex: number) => {
      const player = currentGame.players[wordIndex]
      const playerObj = typeof player === 'string' ? { name: player, surname: player } : player
      const fullPlayerName = showFullPlayerNames ? getFullPlayerName(playerObj) : ''
      const shouldSplit = shouldSplitWord(word)
      const wordParts = shouldSplit ? splitWord(word) : [word]
      
      const isNotRunningVersion = gameConfig.name !== '5runs'

      return (
        <div key={wordIndex} className={`word-row ${shouldSplit ? 'word-row-split' : ''}`}>
          { isNotRunningVersion && showFullPlayerNames && fullPlayerName && (
            <div className="player-name-display">
              {fullPlayerName}
            </div>
          )}
          {wordParts.map((wordPart, partIndex) => (
            <div key={partIndex} className="guess-row">
              {wordPart.split('').map((letter, letterIndex) => {
                const wasGuessed = guessedLetters.has(letter)
                const wasOriginallyGuessed = originallyGuessedLetters.has(letter)
                const isPunctuation = preRevealedChars.has(letter)
                const isSpace = letter === ' '
                
                // Determine if letter should be revealed and what color it should be
                let shouldReveal = false
                let letterClass = ''
                
                if (isSpace) {
                  // Spaces are always revealed with special styling
                  shouldReveal = true
                  letterClass = 'space'
                } else if (isPunctuation) {
                  // Punctuation is always revealed
                  shouldReveal = true
                  letterClass = 'punctuation'
                } else if (isWon) {
                  // When won, show all letters with appropriate colors
                  shouldReveal = true
                  letterClass = wasOriginallyGuessed ? 'revealed' : 'unguessed'
                } else if (isLost) {
                  // When lost, show all letters with orange for unguessed
                  shouldReveal = true
                  letterClass = wasOriginallyGuessed ? 'revealed' : 'unguessed'
                } else if (isThemeGuessing && (allWordsComplete || isOutOfLives)) {
                  // In theme guessing mode, show all letters
                  shouldReveal = true
                  // Always use originallyGuessed vs auto-revealed distinction, regardless of how we got to theme-guessing
                  letterClass = wasOriginallyGuessed ? 'revealed' : 'unguessed'
                } else {
                  // In playing mode, only show letters that have been guessed
                  shouldReveal = wasGuessed
                  letterClass = wasGuessed ? 'revealed' : ''
                }
                
                return (
                  <div 
                    key={letterIndex} 
                    className={`guess-tile ${shouldReveal ? letterClass : 'hidden'}`}
                  >
                    {shouldReveal ? (
                      <span className="letter">{letter}</span>
                    ) : ''}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )
    })
  }

  const renderKey = (letter: string, extraClass: string = '') => (
    <button
      key={letter}
      className={`key ${extraClass} ${getLetterStatus(letter)}`}
      onClick={() => handleLetterClick(letter)}
      disabled={gameStatus !== 'playing' || guessedLetters.has(letter)}
    >
      {letter}
    </button>
  )

  const renderThemeGuessing = () => {
    
    return (
      <div className="theme-guess-container inline">
        {!correctGuess && (
          <>
              <>
                <p style={{ fontSize: '1.5rem', color: '#ff6b6b', fontWeight: 'bold' }}>
                 Guess the {gameConfig.commonThing} to win?
                </p>
              </>
            </>
        )}
        
        {correctGuess ? (
          <div className="winning-message">
            <div className="winning-content">
              <span className="winning-checkmark">✅</span>
              <div className="winning-text">
                <h3>Congratulations!</h3>
                <p>You correctly guessed: <strong>{correctGuess}</strong></p>
              </div>
            </div>
            <div className="mini-stats">
              <div className="mini-stats-grid">
                <div className="mini-stat-item">
                  <div className="mini-stat-value">{currentStats.totalGames}</div>
                  <div className="mini-stat-label">Played</div>
                </div>
                <div className="mini-stat-item">
                  <div className="mini-stat-value">{currentStats.totalGames > 0 ? Math.round((currentStats.wins / currentStats.totalGames) * 100) : 0}%</div>
                  <div className="mini-stat-label">Win Rate</div>
                </div>
                <div className="mini-stat-item">
                  <div className="mini-stat-value">{currentStats.currentWinStreak}</div>
                  <div className="mini-stat-label">Streak</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="theme-input-container">
            <input
              type="text"
              value={themeGuess}
              onChange={(e) => handleThemeInputChange(e.target.value)}
              onKeyDown={handleThemeKeyDown}
              onFocus={() => {
                // Clear any existing value when focused to prevent unwanted letters
                if (themeGuess.length === 1) {
                  setThemeGuess('')
                }
              }}
              placeholder="Type to search themes..."
              className="theme-input"
              autoFocus
            />
            
            {showThemeOptions && getFilteredThemeOptions().length > 0 && (
              <div className="theme-options">
                {getFilteredThemeOptions().slice(0, gameConfig.dataSource === 'running' ? 6 : 3).map((option, index) => (
                  <button
                    key={index}
                    className={`theme-option ${index === selectedOptionIndex ? 'selected' : ''}`}
                    onClick={() => handleThemeGuess(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        
        {incorrectThemeGuesses.length > 0 && (
          <div className="incorrect-guesses">
            <div className="incorrect-guesses-list">
              {incorrectThemeGuesses.map((guess, index) => (
                <div key={index} className="incorrect-guess-item">
                  <span className="incorrect-guess-cross">❌</span>
                  <span className="incorrect-guess-text">{guess}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="game-container">
      <div className="content-area">
        {/* Lives display */}
        <div className="lives-display">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className={`life-icon ${index >= livesRemaining ? 'lost' : 'remaining'}`}>
              <span className="soccer-ball">{gameConfig.theme.icon}</span>
              {index >= livesRemaining && <span className="cross-overlay">❌</span>}
            </div>
          ))}
        </div>
        
        <div 
          className={`words-grid ${gameStatus === 'theme-guess' ? 'compact' : ''}`}
          data-game-status={gameStatus}
        >
          {renderWordGrid()}
        </div>

        {gameStatus === 'lost' && (
          <div className="game-over-section">
            <div className="game-message lose">😔 Too Bad, you didn't get it today!<br />The {gameConfig.commonThing} was "{correctTheme}".</div>
            <div className="mini-stats">
              <div className="mini-stats-grid">
                <div className="mini-stat-item">
                  <div className="mini-stat-value">{currentStats.totalGames}</div>
                  <div className="mini-stat-label">Played</div>
                </div>
                <div className="mini-stat-item">
                  <div className="mini-stat-value">{currentStats.totalGames > 0 ? Math.round((currentStats.wins / currentStats.totalGames) * 100) : 0}%</div>
                  <div className="mini-stat-label">Win Rate</div>
                </div>
                <div className="mini-stat-item">
                  <div className="mini-stat-value">{currentStats.currentWinStreak}</div>
                  <div className="mini-stat-label">Streak</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {gameStatus === 'won' && (
          <div className="game-over-section">
            <div className="game-message win">GREAT JOB TODAY!<br />The {gameConfig.commonThing} was "{correctTheme}".</div>
            <div className="mini-stats">
              <div className="mini-stats-grid">
                <div className="mini-stat-item">
                  <div className="mini-stat-value">{currentStats.totalGames}</div>
                  <div className="mini-stat-label">Played</div>
                </div>
                <div className="mini-stat-item">
                  <div className="mini-stat-value">{currentStats.totalGames > 0 ? Math.round((currentStats.wins / currentStats.totalGames) * 100) : 0}%</div>
                  <div className="mini-stat-label">Win Rate</div>
                </div>
                <div className="mini-stat-item">
                  <div className="mini-stat-value">{currentStats.currentWinStreak}</div>
                  <div className="mini-stat-label">Streak</div>
                </div>
              </div>
            </div>
          </div>
        )}
        {gameStatus === 'theme-guess' && renderThemeGuessing()}
      </div>

      {gameStatus === 'playing' && (
        <>
          <div 
            ref={keyboardRef}
            className="keyboard"
            tabIndex={-1}
            style={{ outline: 'none' }}
          >
            <div className="keyboard-row">
              {topRow.map(letter => renderKey(letter))}
            </div>
            
            <div className="keyboard-row">
              {middleRow.map(letter => renderKey(letter))}
            </div>
            
            <div className="keyboard-row">
              {bottomRow.map(letter => renderKey(letter))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Game
